import React, { useState, useEffect } from "react";
import api from "../../api/axios";
import { Plus, Save, ArrowLeft, Trash2 } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { formatCurrencyCOP, cleanNumber, formatNumberWithDots, } from "../../utils/format";
import Swal from "sweetalert2";


const RegisterEntry = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [presentations, setPresentations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [entryDate, setEntryDate] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [details, setDetails] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const DEFAULT_NR = "NR";
  const DEFAULT_EXPIRATION_DATE = "1901-01-01";

  const supplierOptions = suppliers.map((s) => ({
    value: s.id,
    label: s.name,
  }));

  const productOptions = products.map((p) => ({
    value: p.id,
    label: p.name
  }));
  const brandOptions = brands.map((b) => ({
    value: b.id,
    label: b.name
  }));

  const presentationOptions = presentations.map((p) => ({
    value: p.id,
    label: p.name
  }));


  const [form, setForm] = useState({
    productId: "",
    brandId: "",
    presentationId: "",
    invimaCode: "",
    batchNumber: "",
    expirationDate: "",
    unitPrice: "",
    quantity: "",
  });

  const [errors, setErrors] = useState({});
  const [totalPurchase, setTotalPurchase] = useState(0);

  useEffect(() => {
    api.get("/products").then((res) => setProducts(res.data));
    api.get("/brands").then((res) => setBrands(res.data));
    api.get("/presentations").then((res) => setPresentations(res.data));
    api.get("/suppliers").then((res) => setSuppliers(res.data));
  }, []);

  // ✅ Validaciones locales
  const validateForm = () => {
    const newErrors = {};
    if (!entryDate) newErrors.entryDate = "La fecha de entrada es obligatoria";
    if (!supplierId) newErrors.supplierId = "El proveedor es obligatorio";
    if (!form.productId) newErrors.productId = "Debes seleccionar un producto";
    if (!form.quantity || parseInt(form.quantity) <= 0)
      newErrors.quantity = "La cantidad debe ser mayor a 0";
    if (!form.unitPrice || parseFloat(form.unitPrice) <= 0)
      newErrors.unitPrice = "El precio unitario debe ser mayor a 0";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Agregar producto a la tabla dinámica
  const handleAddDetail = () => {
    if (!validateForm()) return;

    const totalProduct =
      parseFloat(form.unitPrice) * parseInt(form.quantity);

    const normalizedBrandId = form.brandId || null;
    const normalizedPresentationId = form.presentationId || null;

    const newDetail = {
      productId: form.productId,
      brandId: normalizedBrandId,
      presentationId: normalizedPresentationId,

      invimaCode: form.invimaCode.trim() || DEFAULT_NR,
      batchNumber: form.batchNumber.trim() || DEFAULT_NR,
      expirationDate: form.expirationDate || DEFAULT_EXPIRATION_DATE,

      unitPrice: parseFloat(form.unitPrice),
      quantity: parseInt(form.quantity),
      totalProduct,

      productName:
        products.find(p => p.id === parseInt(form.productId))?.name || DEFAULT_NR,

      brandName:
        brands.find(b => b.id === parseInt(form.brandId))?.name || DEFAULT_NR,

      presentationName:
        presentations.find(p => p.id === parseInt(form.presentationId))?.name ||
        DEFAULT_NR,
    };

    setDetails(prev => [...prev, newDetail]);
    setTotalPurchase(prev => prev + totalProduct);

    setForm({
      productId: "",
      brandId: "",
      presentationId: "",
      invimaCode: "",
      batchNumber: "",
      expirationDate: "",
      unitPrice: "",
      quantity: "",
    });

    setErrors({});
  };

  // ✅ Remover detalle de la tabla
  const handleRemoveDetail = (index) => {
    const removedDetail = details[index];
    setDetails(prev => prev.filter((_, i) => i !== index));
    setTotalPurchase(prev => prev - removedDetail.totalProduct);
    toast.info("Producto removido de la lista");
  };

  const decreaseQuantity = (index) => {
    const detail = details[index];
    const newQuantity = detail.quantity - 1;
    if (newQuantity <= 0) {
      handleRemoveDetail(index);
    } else {
      const updatedDetails = [...details];
      updatedDetails[index].quantity = newQuantity;
      updatedDetails[index].totalProduct = updatedDetails[index].unitPrice * newQuantity;
      setDetails(updatedDetails);
      setTotalPurchase(prev => prev - detail.unitPrice);
    }
  };

  const increaseQuantity = (index) => {
    const detail = details[index];
    const newQuantity = detail.quantity + 1;
    const updatedDetails = [...details];
    updatedDetails[index].quantity = newQuantity;
    updatedDetails[index].totalProduct = updatedDetails[index].unitPrice * newQuantity;
    setDetails(updatedDetails);
    setTotalPurchase(prev => prev + detail.unitPrice);
  };

  // Función que se dispara al dar click en "Guardar Entrada"
  const handlePreSubmit = (e) => {
    e.preventDefault();
    if (details.length === 0) {
      return toast.error("Debes agregar al menos un producto");
    }
    if (!entryDate || !supplierId) {
      return toast.error("Fecha y Proveedor son obligatorios");
    }
    setShowConfirmModal(true); // Abrimos el modal de confirmación
  };

  // ✅ Guardar en la base de datos
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setShowConfirmModal(false); // Cerramos el modal de pregunta

    try {
      const userId = 1;
      // 1. Crear la entrada
      const entryRes = await api.post("/entries", {
        date: entryDate,
        supplier_id: supplierId,
        user_id: userId,
        total_purchase: totalPurchase,
      });

      const entryId = entryRes.data.entry.id;

      // 2. Registrar detalles (Bucle)
      for (const detail of details) {
        const variantRes = await api.post("/product-variants", {
          product_id: detail.productId,
          brand_id: detail.brandId,
          presentation_id: detail.presentationId,
          batch_number: detail.batchNumber,
          expiration_date: detail.expirationDate,
          invima_code: detail.invimaCode,
          unit_price: detail.unitPrice,
        });

        const variantId = variantRes.data.variant?.id;

        await api.post("/entry-details", {
          entry_id: entryId,
          product_id: detail.productId,
          variant_id: variantId,
          quantity: detail.quantity,
          unit_price: detail.unitPrice,
          total_product: detail.totalProduct,
          user_id: userId,
        });
      }

      // ✅ PASO FINAL: Animación de Éxito y Redirección
      Swal.fire({
        title: "¡Registrado!",
        text: "La entrada se ha guardado correctamente",
        icon: "success",
        timer: 2000, // Dura 2 segundos
        showConfirmButton: false,
        willClose: () => {
          navigate("/entries");
        }
      });

    } catch (error) {
      console.error(error);
      toast.error("Error al procesar el registro");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-md max-w-7xl mx-auto">
      <ToastContainer />

      <button
        onClick={() => navigate("/entries")}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft size={20} />
        Volver a entradas
      </button>

      <h2 className="text-2xl text-center font-bold mb-4">Registrar Entrada</h2>

      {/* Formulario superior */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="font-semibold">Fecha de Entrada:</label>
          <input
            type="date"
            value={entryDate}
            onChange={(e) => setEntryDate(e.target.value)}
            className={`w-full mt-1 border rounded px-3 py-2 ${errors.entryDate ? "border-red-500" : "border-gray-300"
              }`}
          />
          {errors.entryDate && (
            <p className="text-red-500 text-sm mt-1">{errors.entryDate}</p>
          )}
        </div>
        <div>
          <label className="font-semibold">Proveedor:</label>

          <Select
            options={supplierOptions}
            placeholder="Seleccionar proveedor..."
            value={supplierOptions.find(o => o.value === supplierId) || null}
            onChange={(option) =>
              setSupplierId(option?.value || "")
            }
            className="mt-1"
            classNamePrefix="react-select"
          />

          {errors.supplierId && (
            <p className="text-red-500 text-sm mt-1">
              {errors.supplierId}
            </p>
          )}
        </div>

      </div>

      <hr />

      <h3 className="text-xl text-center font-semibold mt-4 mb-4">
        Agregar Producto
      </h3>

      {/* Formulario de detalle */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="font-semibold">Producto:</label>
          <Select
            options={productOptions}
            placeholder="Seleccionar producto..."
            value={productOptions.find(o => o.value === form.productId) || null}
            onChange={(option) =>
              setForm({ ...form, productId: option?.value || "" })
            }
            className="mt-1"
            classNamePrefix="react-select"
          />
          {errors.productId && (
            <p className="text-red-500 text-sm mt-1">{errors.productId}</p>
          )}

        </div>

        <div>
          <label className="font-semibold">Marca:</label>
          <Select
            options={brandOptions}
            placeholder="Seleccionar marca..."
            value={
              brandOptions.find(o => o.value === form.brandId) || null
            }
            onChange={(option) =>
              setForm({ ...form, brandId: option?.value || "" })
            }
            className="mt-1"
            classNamePrefix="react-select"
          />
          {errors.brandId && (
            <p className="text-red-500 text-sm mt-1">
              {errors.brandId}
            </p>
          )}
        </div>


        <div>
          <label className="font-semibold">Presentación:</label>
          <Select
            options={presentationOptions}
            placeholder="Seleccionar presentación..."
            value={
              presentationOptions.find(
                o => o.value === form.presentationId
              ) || null
            }
            onChange={(option) =>
              setForm({
                ...form,
                presentationId: option?.value || ""
              })
            }
            className="mt-1"
            classNamePrefix="react-select"
          />
          {errors.presentationId && (
            <p className="text-red-500 text-sm mt-1">
              {errors.presentationId}
            </p>
          )}
        </div>


        <div>
          <label className="font-semibold">Código INVIMA:</label>
          <input
            type="text"
            placeholder="Ej: 2023M-001234"
            value={form.invimaCode}
            onChange={(e) => setForm({ ...form, invimaCode: e.target.value })}
            className={`w-full mt-1 border rounded px-3 py-2 ${errors.invimaCode ? "border-red-500" : "border-gray-300"
              }`}
          />
          {errors.invimaCode && (
            <p className="text-red-500 text-sm mt-1">{errors.invimaCode}</p>
          )}
        </div>

        <div>
          <label className="font-semibold">Lote:</label>
          <input
            type="text"
            placeholder="Ej: LT-45678"
            value={form.batchNumber}
            onChange={(e) => setForm({ ...form, batchNumber: e.target.value })}
            className={`w-full mt-1 border rounded px-3 py-2 ${errors.batchNumber ? "border-red-500" : "border-gray-300"
              }`}
          />
          {errors.batchNumber && (
            <p className="text-red-500 text-sm mt-1">{errors.batchNumber}</p>
          )}
        </div>

        <div>
          <label className="font-semibold">Fecha de Vencimiento:</label>
          <input
            type="date"
            value={form.expirationDate}
            onChange={(e) =>
              setForm({ ...form, expirationDate: e.target.value })
            }
            className={`w-full mt-1 border rounded px-3 py-2 ${errors.expirationDate ? "border-red-500" : "border-gray-300"
              }`}
          />
          {errors.expirationDate && (
            <p className="text-red-500 text-sm mt-1">
              {errors.expirationDate}
            </p>
          )}
        </div>

        <div>
          <label className="font-semibold">Precio Unitario:</label>
          <input
            type="text"
            inputMode="numeric"
            placeholder="Ej: 1.200.000"
            value={formatNumberWithDots(form.unitPrice)}
            onChange={(e) =>
              setForm({
                ...form,
                unitPrice: cleanNumber(e.target.value),
              })
            }
            className={`w-full mt-1 border rounded px-3 py-2 ${errors.unitPrice ? "border-red-500" : "border-gray-300"
              }`}
          />
          {errors.unitPrice && (
            <p className="text-red-500 text-sm mt-1">{errors.unitPrice}</p>
          )}
        </div>


        <div>
          <label className="font-semibold">Cantidad:</label>
          <input
            type="number"
            placeholder="Ej: 100"
            value={form.quantity}
            onChange={(e) => setForm({ ...form, quantity: e.target.value })}
            className={`w-full mt-1 border rounded px-3 py-2 ${errors.quantity ? "border-red-500" : "border-gray-300"
              }`}
          />
          {errors.quantity && (
            <p className="text-red-500 text-sm mt-1">{errors.quantity}</p>
          )}
        </div>
      </div>

      <button
        onClick={handleAddDetail}
        className="mt-4 bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-blue-700"
      >
        <Plus size={18} />
        Agregar Producto
      </button>

      {/* Tabla de detalles */}
      <div className="overflow-x-auto rounded-xl shadow mt-6">
        <table className="min-w-full bg-white table-auto text-center">
          <thead>
            <tr className="bg-gray-100 text-gray-700 text-center text-sm uppercase tracking-wider">
              <th className="px-6 py-3">Producto</th>
              <th className="px-6 py-3">Marca</th>
              <th className="px-6 py-3">Presentación</th>
              <th className="px-6 py-3">INVIMA</th>
              <th className="px-6 py-3">Lote</th>
              <th className="px-6 py-3">Vencimiento</th>
              <th className="px-6 py-3">Cantidad</th>
              <th className="px-6 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {details.length === 0 ? (
              <tr>
                <td
                  colSpan="8"
                  className="px-6 py-6 text-center text-gray-400 italic"
                >
                  No hay registros.
                </td>
              </tr>
            ) : (
              details.map((d, i) => (
                <tr
                  key={i}
                  className={`border-t ${i % 2 === 0 ? "bg-white" : "bg-gray-50"
                    }`}
                >
                  <td className="px-6 py-4">{d.productName}</td>
                  <td className="px-6 py-4">{d.brandName}</td>
                  <td className="px-6 py-4">{d.presentationName}</td>
                  <td className="px-6 py-4">{d.invimaCode}</td>
                  <td className="px-6 py-4">{d.batchNumber}</td>
                  <td className="px-6 py-4">
                    {d.expirationDate === DEFAULT_EXPIRATION_DATE
                      ? "1901-01-01"
                      : d.expirationDate
                      ? new Date(d.expirationDate).toLocaleDateString("es-CO", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })
                      : "-"}
                  </td>
                  <td className="px-6 py-4 flex items-center justify-center gap-2">
                    <button
                      onClick={() => decreaseQuantity(i)}
                      className="p-1 text-red-300 hover:bg-red-500 rounded"
                      title="Disminuir cantidad"
                    >
                      -
                    </button>
                    <span className="font-semibold text-blue-600">{d.quantity}</span>
                    <button
                      onClick={() => increaseQuantity(i)}
                      className="p-1 text-green-300 hover:bg-green-500 rounded"
                      title="Aumentar cantidad"
                    >
                      +
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleRemoveDetail(i)}
                      className="p-2 text-red-500 hover:bg-red-600 hover:text-white rounded-lg transition-all duration-200"
                      title="Eliminar de la lista"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <h4 className="text-right text-lg font-bold mt-4">
        Total: {formatCurrencyCOP(totalPurchase)}
      </h4>

     {/* Botón de Guardar */}
      <div className="flex justify-end mt-6">
        <button
          onClick={handlePreSubmit}
          disabled={isSubmitting}
          className={`w-full md:w-auto px-6 py-2 rounded flex items-center justify-center gap-2 text-white transition ${
            isSubmitting ? "bg-gray-400" : "bg-green-600 hover:bg-green-700"
          }`}
        >
          <Save size={18} />
          {isSubmitting ? "Procesando..." : "Guardar Entrada"}
        </button>
      </div>

      {/* MODAL DE CONFIRMACIÓN - Debe estar DENTRO del div principal o de un fragmento */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-sm w-full mx-4 transform transition-all scale-100">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                <Save className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">¿Confirmar registro?</h3>
              <p className="text-sm text-gray-500 mt-2">
                Se guardará la entrada con {details.length} productos por un total de {formatCurrencyCOP(totalPurchase)}.
              </p>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-semibold transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition"
              >
                Sí, guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div> // Este es el cierre del DIV principal que abriste al inicio del componente
  );
};

export default RegisterEntry;
